import { makeRequest } from '../makeRequest';
import {
	VariableGroup,
	Variable,
	CategoryMultipleVariable,
	CategoryFixedVariable,
	CategoryNonFixedVariable,
	CategoryMultipleNonFixedVariable,
	VariableSeries,
	Order
} from '../../types';
import { useQuery } from '@tanstack/react-query';
import { decodeURIComponentSafe } from 'helpers/generic';

export type VariablesData = {
	variables: Record<string, Variable>;
	order: Order[];
	groups: Record<string, VariableGroup>;
	sets: Record<string, VariableSeries>;
};

export const useVariablesQuery = ({ projectId }: { projectId: string }) => {
	return useQuery({
		queryKey: ['variables', projectId],
		queryFn: async () => {
			const result = await makeRequest<VariablesData>({
				method: 'getVariablesData',
				service: 'stats',
				data: {
					projectId: parseInt(projectId)
				}
			});

			return decodeVariablesData(result);
		}
	});
};

/**
 * Decodes the URL-encoded fields of a raw `getVariablesData` response. Shared between the
 * query and the project-metadata export so the two can't drift.
 */
export const decodeVariablesData = (data: VariablesData): VariablesData => {
	return {
		...data,
		variables: Object.fromEntries(
			Object.entries(data.variables).map(([key, variable]) => [key, decodeVariable(variable)])
		)
	};
};

export const decodeVariable = (variable: Variable): Variable => {
	switch (variable.variableType) {
		case 'category':
			if (variable.fixedCategories === 'yes') {
				return {
					...variable,
					description: decodeURIComponentSafe(variable.description),
					variableLabel: decodeURIComponentSafe(variable.variableLabel),
					allowedCategories: variable.allowedCategories.map(category => ({
						...category,
						label: decodeURIComponentSafe(category.label),
						description: decodeURIComponentSafe(category.description),
						value: decodeURIComponentSafe(category.value)
					}))
				} as CategoryFixedVariable;
			}

			return {
				...variable,
				description: decodeURIComponentSafe(variable.description),
				variableLabel: decodeURIComponentSafe(variable.variableLabel),
				categories: variable.categories.map(category => decodeURIComponentSafe(category))
			} as CategoryNonFixedVariable;

		case 'categoryMultiple':
			if (variable.fixedCategories === 'yes') {
				return {
					...variable,
					description: decodeURIComponentSafe(variable.description),
					variableLabel: decodeURIComponentSafe(variable.variableLabel),
					allowedCategories: variable.allowedCategories.map(category => ({
						...category,
						label: decodeURIComponentSafe(category.label),
						description: decodeURIComponentSafe(category.description),
						value: decodeURIComponentSafe(category.value)
					}))
				} as CategoryMultipleVariable;
			}

			return {
				...variable,
				description: decodeURIComponentSafe(variable.description),
				variableLabel: decodeURIComponentSafe(variable.variableLabel),
				categories: variable.categories.map(category => decodeURIComponentSafe(category))
			} as CategoryMultipleNonFixedVariable;

		default:
			return {
				...variable,
				description: decodeURIComponentSafe(variable.description),
				variableLabel: decodeURIComponentSafe(variable.variableLabel)
			};
	}
};
